import { CARD_H, CARD_W } from "@webscan/constants.js";
import { homographyToQuad } from "@webscan/warp.js";
import { PAD_VALUE, type LetterboxedFrame } from "./pose";
import { warn } from "../shared/debug";
import { pixelSize, type PixelSource } from "./camera-frame";
import {
  applyHomography,
  windowHomography,
  type SourceWindow,
  type ViewRect,
  type Homography,
} from "../shared/geometry";

/**
 * Both readbacks the scan loop needs, done in WebGL rather than on a 2D canvas.
 *
 * The measurement that motivates this: the GPU warp uploads a 2160x3840 video frame as a
 * texture, draws a projective quad and reads back 488x680 — **6-8 ms**. The 2D-canvas
 * letterbox draws the same video and reads back a comparable 640x640 — **30-75 ms**. The
 * drawing is not the cost; `getImageData` is. WebGL's `readPixels` at the same size is
 * roughly five times faster on this hardware, and neither `willReadFrequently` setting
 * closes the gap: a software canvas moves the cost to `drawImage` instead.
 *
 * One program serves both jobs, because a letterbox is just the affine special case of a
 * projective sample. Each output size gets its own context and its own reusable readback
 * buffer.
 */

const VERTEX = `
attribute vec2 a_clip;
attribute vec2 a_canon;
uniform mat3 u_h;
varying vec3 v_hp;
void main() {
  v_hp = u_h * vec3(a_canon, 1.0);
  gl_Position = vec4(a_clip, 0.0, 1.0);
}`;

const FRAGMENT = `
precision highp float;
uniform sampler2D u_tex;
uniform vec2 u_size;
varying vec3 v_hp;
void main() {
  vec2 src = v_hp.xy / v_hp.z;
  // +0.5 puts the sample on the texel centre, matching how the CPU path indexes pixels.
  gl_FragColor = vec4(texture2D(u_tex, (src + 0.5) / u_size).rgb, 1.0);
}`;

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("could not create shader");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) || "shader compile failed");
  }
  return shader;
}

/** A destination rectangle in output pixels. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class GlSampler {
  private readonly gl: WebGLRenderingContext;
  private readonly texture: WebGLTexture;
  private readonly clipBuffer: WebGLBuffer;
  private readonly canonBuffer: WebGLBuffer;
  private readonly uH: WebGLUniformLocation;
  private readonly uSize: WebGLUniformLocation;
  /** One readback buffer per output size, kept rather than reallocated. */
  private readonly buffers = new Map<string, Uint8Array>();
  private readonly clip = new Float32Array(8);
  private readonly canon = new Float32Array(8);
  /** Dimensions the texture was last allocated at, so updates can reuse the storage. */
  private textureWidth = 0;
  private textureHeight = 0;
  /** Set when the driver drops the context; every later call then declines cleanly. */
  private lost = false;

  private constructor(
    gl: WebGLRenderingContext,
    program: WebGLProgram,
    texture: WebGLTexture,
    clipBuffer: WebGLBuffer,
    canonBuffer: WebGLBuffer,
    readonly width: number,
    readonly height: number,
  ) {
    this.gl = gl;
    this.texture = texture;
    this.clipBuffer = clipBuffer;
    this.canonBuffer = canonBuffer;
    this.uH = gl.getUniformLocation(program, "u_h")!;
    this.uSize = gl.getUniformLocation(program, "u_size")!;
    // A lost context is the one failure mode that otherwise leaves no trace: the app
    // simply stops producing cards. Say so, loudly, and stop pretending to work.
    gl.canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      if (this.lost) return;
      this.lost = true;
      warn("detect", "WebGL context lost — GPU memory pressure; falling back to canvas");
    });
    gl.canvas.addEventListener("webglcontextrestored", () => {
      warn("detect", "WebGL context restored, but this sampler stays disabled");
    });
  }

  private buffer(width: number, height: number): Uint8Array {
    const key = `${width}x${height}`;
    let found = this.buffers.get(key);
    if (!found) {
      found = new Uint8Array(width * height * 4);
      this.buffers.set(key, found);
    }
    return found;
  }

  /**
   * One sampler for every job, sized to the largest of them.
   *
   * Previously each output size got its own context, and each context kept its own
   * full-resolution copy of the video — 33 MB per frame, twice, next to a WebGPU runtime.
   * iOS Safari's GPU budget is far tighter than a desktop's and the tab was being killed
   * a few seconds into scanning, with nothing in the log because the process was gone.
   *
   * A single context draws each job into the bottom-left of one framebuffer with its own
   * viewport, and reads back exactly that rectangle. Halves the resident texture memory
   * and the per-frame upload.
   */
  static create(width: number, height: number): GlSampler | null {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const gl = canvas.getContext("webgl", {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false,
        // Read back immediately after drawing, so there is nothing for the compositor
        // to schedule around.
        desynchronized: true,
      }) as WebGLRenderingContext | null;
      if (!gl) return null;

      const program = gl.createProgram();
      if (!program) return null;
      gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX));
      gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || "link failed");
      }
      gl.useProgram(program);

      const clipBuffer = gl.createBuffer();
      const canonBuffer = gl.createBuffer();
      if (!clipBuffer || !canonBuffer) return null;
      for (const [name, buffer] of [
        ["a_clip", clipBuffer],
        ["a_canon", canonBuffer],
      ] as const) {
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, 32, gl.DYNAMIC_DRAW);
        const location = gl.getAttribLocation(program, name);
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
      }

      const texture = gl.createTexture();
      if (!texture) return null;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      // CLAMP_TO_EDGE and LINEAR mirror the CPU path's edge replication and bilinear read.
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.viewport(0, 0, width, height);
      return new GlSampler(gl, program, texture, clipBuffer, canonBuffer, width, height);
    } catch {
      return null;
    }
  }

  /**
   * Sample the video into `dst`, mapping output pixels through `h` (row-major 3x3).
   *
   * `h` takes an output-pixel coordinate to a video-pixel coordinate. Because `H · p` is
   * affine in that coordinate, interpolating its three components across the quad is
   * exact and the fragment shader only has to divide — no per-pixel matrix work.
   */
  private draw(
    video: PixelSource,
    out: { width: number; height: number },
    dst: Rect,
    h: number[],
    clear: number | null,
    reuseFrame = false,
    span?: { w: number; h: number },
  ): Uint8Array | null {
    const gl = this.gl;
    const sensor = pixelSize(video);
    if (this.lost || gl.isContextLost()) return null;

    try {
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      // `texImage2D` reallocates the texture; at 2160x3840 that is 33 MB of fresh GPU
      // storage every frame, and freeing it is the driver's problem rather than ours.
      // `texSubImage2D` writes into the allocation already there.
      //
      // `reuseFrame` skips the upload entirely and samples whatever is resident — the
      // upload is the expensive half of this path (a 4K YUV-to-RGBA conversion per
      // call on iOS), and on a settle frame the letterbox has already paid it a few
      // dozen milliseconds ago. Reusing is also *more correct* for the warp: the quad
      // was detected on the resident frame, not on the one the camera shows now.
      if (this.textureWidth !== sensor.width || this.textureHeight !== sensor.height) {
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          video as TexImageSource,
        );
        this.textureWidth = sensor.width;
        this.textureHeight = sensor.height;
      } else if (!reuseFrame) {
        gl.texSubImage2D(
          gl.TEXTURE_2D,
          0,
          0,
          0,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          video as TexImageSource,
        );
      }
    } catch {
      // Safari throws rather than erroring if the frame is not yet decodable.
      return null;
    }

    // Each job owns the bottom-left corner of the shared framebuffer.
    gl.viewport(0, 0, out.width, out.height);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(0, 0, out.width, out.height);

    if (clear !== null) {
      gl.clearColor(clear / 255, clear / 255, clear / 255, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    // Clip-space y increases with output y, so `readPixels` — which returns rows from the
    // bottom of the framebuffer up — hands back output row 0 first and no flip is needed.
    const x0 = (2 * dst.x) / out.width - 1;
    const x1 = (2 * (dst.x + dst.w)) / out.width - 1;
    const y0 = (2 * dst.y) / out.height - 1;
    const y1 = (2 * (dst.y + dst.h)) / out.height - 1;
    this.clip.set([x0, y0, x1, y0, x1, y1, x0, y1]);
    // The rasteriser samples at fragment centres, so spanning the canonical coordinate
    // from -0.5 to w-0.5 puts output pixel `i` exactly on canonical `i`. A `span` decouples
    // the canonical extent from the output size: `h` keeps meaning what it meant at
    // span.w x span.h while the dst rect samples that same map at a denser grid.
    const cw = span?.w ?? dst.w;
    const ch = span?.h ?? dst.h;
    this.canon.set([-0.5, -0.5, cw - 0.5, -0.5, cw - 0.5, ch - 0.5, -0.5, ch - 0.5]);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.clipBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.clip);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.canonBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.canon);

    // GLSL matrices are column-major and WebGL 1 forbids `transpose`, so the row-major
    // solve is reordered here rather than in the shader.
    gl.uniformMatrix3fv(this.uH, false, [h[0], h[3], h[6], h[1], h[4], h[7], h[2], h[5], h[8]]);
    gl.uniform2f(this.uSize, sensor.width, sensor.height);
    gl.drawArrays(gl.TRIANGLE_FAN, 0, 4);
    const pixels = this.buffer(out.width, out.height);
    gl.readPixels(0, 0, out.width, out.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.disable(gl.SCISSOR_TEST);
    return pixels;
  }

  /**
   * Rectify `quad` — in scan-window pixels — straight out of the live video.
   *
   * Geometry comes from `homographyToQuad`, the same solve the CPU warp uses, so the two
   * agree on where the corners are to floating-point noise. They will not agree
   * bit-for-bit on pixels: this is the GPU's bilinear filter rather than the ported one.
   */
  warpCard(
    video: PixelSource,
    window: SourceWindow,
    quad: Float32Array,
    scale = 1,
    reuseFrame = true,
  ): Uint8ClampedArray | null {
    // The detector works in window coordinates; the texture is the whole video frame.
    // Through the full projective map: the card's own homography composed with the
    // window's is still a homography, and both agree on these four corners — so
    // rectifying from the sensor through `homographyToQuad` stays exact under the
    // keystone, not just under rotation.
    const toSensor = windowHomography(window);
    const inVideo = new Float32Array(8);
    for (let i = 0; i < 8; i += 2) {
      const [x, y] = applyHomography(toSensor, quad[i], quad[i + 1]);
      inVideo[i] = x;
      inVideo[i + 1] = y;
    }
    const h = homographyToQuad(inVideo);
    if (!h) return null;
    const w = Math.round(CARD_W * scale);
    const height = Math.round(CARD_H * scale);
    if (w > this.width || height > this.height) return null;
    // The letterbox of this same loop iteration uploaded the frame the quad was
    // detected on; sample that instead of paying a second 4K upload for a newer one.
    // The canonical span stays 488x680 whatever the output size — the homography is
    // defined in that space, and a scaled output just samples it more densely.
    const pixels = this.draw(
      video,
      { width: w, height: height },
      { x: 0, y: 0, w, h: height },
      h,
      null,
      reuseFrame,
      { w: CARD_W, h: CARD_H },
    );
    return pixels ? new Uint8ClampedArray(pixels.buffer, 0, pixels.length) : null;
  }

  /** A small sensor strip. The canonical warp and hash pixels keep their existing path. */
  warpRegion(
    video: PixelSource,
    width: number,
    height: number,
    matrix: Homography,
  ): Uint8ClampedArray | null {
    if (width > this.width || height > this.height) return null;
    const pixels = this.draw(
      video,
      { width, height },
      { x: 0, y: 0, w: width, h: height },
      [...matrix],
      null,
      true,
    );
    return pixels ? new Uint8ClampedArray(pixels.buffer, 0, pixels.length) : null;
  }

  /** A GPU detector has not uploaded this frame to GL; do so only for a read. */
  refreshFrame(video: PixelSource): void {
    this.draw(
      video,
      { width: 1, height: 1 },
      { x: 0, y: 0, w: 1, h: 1 },
      [1, 0, 0, 0, 1, 0, 0, 0, 1],
      null,
    );
  }

  /** Keep no full sensor texture resident between reads when detection uses WebGPU. */
  releaseVideo(): void {
    if (this.lost || this.textureWidth <= 1) return;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    this.textureWidth = this.textureHeight = 1;
  }

  dispose(): void {
    if (this.lost) return;
    this.lost = true;
    this.gl.deleteTexture(this.texture);
    this.gl.deleteBuffer(this.clipBuffer);
    this.gl.deleteBuffer(this.canonBuffer);
    this.buffers.clear();
    this.gl.getExtension("WEBGL_lose_context")?.loseContext();
  }

  /**
   * Fit the scan window — or a sub-rectangle of it — into the model's raster, padded to
   * grey.
   *
   * The arithmetic follows ultralytics, `round(d - 0.1)` included, because the quads come
   * back in this raster's pixels and any disagreement lands on the corners.
   *
   * `area` is the detection area in view pixels; omitted, it is the whole window. It
   * enters as a translation on the source side of the window's own map, so the quads
   * come back in the area's pixels and the caller only has to add its origin to put them
   * back in view space. Nothing else about the sample changes — under desk view the
   * composition is still one homography, which this shader has always divided.
   */
  letterbox(
    video: PixelSource,
    window: SourceWindow,
    size: number,
    area?: ViewRect,
  ): LetterboxedFrame | null {
    const sx = area?.x ?? 0;
    const sy = area?.y ?? 0;
    const sw = area?.w ?? window.sw;
    const sh = area?.h ?? window.sh;
    const scale = Math.min(size / sh, size / sw);
    const w = Math.round(sw * scale);
    const h = Math.round(sh * scale);
    const left = Math.round((size - w) / 2 - 0.1);
    const top = Math.round((size - h) / 2 - 0.1);

    // Scale into area pixels, offset to where the area sits in the view, then the
    // window's full map into the sensor — with a keystone that map is genuinely
    // projective, which this shader has always handled: it divides. Subtracting half the
    // bottom row converts the window's edge-space origin into the texel-index space the
    // shader samples in, *after* the divide.
    const [m0, m1, m2, m3, m4, m5, m6, m7, m8] = windowHomography(window);
    const kx = sw / w;
    const ky = sh / h;
    const p6 = m6 * kx;
    const p7 = m7 * ky;
    const p8 = m6 * sx + m7 * sy + m8;
    const matrix = [
      m0 * kx - 0.5 * p6,
      m1 * ky - 0.5 * p7,
      m0 * sx + m1 * sy + m2 - 0.5 * p8,
      m3 * kx - 0.5 * p6,
      m4 * ky - 0.5 * p7,
      m3 * sx + m4 * sy + m5 - 0.5 * p8,
      p6,
      p7,
      p8,
    ];
    const pixels = this.draw(
      video,
      { width: size, height: size },
      { x: left, y: top, w, h },
      matrix,
      PAD_VALUE,
    );
    if (!pixels) return null;
    return {
      data: new Uint8ClampedArray(pixels.buffer, 0, pixels.length),
      scale,
      left,
      top,
    };
  }
}
